(() => {
  'use strict';
  // Config comes from snippets/posthog-analytics.liquid, which renders nothing without a token.
  const configElement = document.getElementById('PostHogConfig');
  if (!configElement || window.lampsPreviewBuild || window.posthog?.__SV) return;
  let config;
  try { config = JSON.parse(configElement.textContent); } catch { return; }
  if (!config?.token) return;

  // The GA4 events PostHog gets. Each call site already sends them through gtag(), so they
  // are read off window.dataLayer here instead of being sent twice from every caller.
  // Product, cart and checkout events come from the custom pixel in pixels/posthog-ecommerce.js.
  const CART_EVENTS = [
    'quantity_change', 'coupon_applied', 'quick_add_click', 'category_rail_click', 'search_result_click', 'facet_apply',
    'view_item_list', 'filter_apply', 'filter_clear', 'sort_changed', 'pagination_click', 'view_mode_change', 'collection_scroll_depth',
    'add_to_wishlist', 'remove_from_wishlist', 'notify_me_click', 'size_guide_open', 'add_to_compare', 'read_buying_guide', 'view_trade_program',
    'page_not_found', 'chat_open', 'popup_view', 'view_cart',
    'hero_banner_click', 'promo_banner_click', 'grid_banner_click', 'collection_tile_click', 'collection_banner_click', 'collection_hero_click',
  ];
  const forwarded = (name) => /^(room_|lf_|menu_|resource_)/.test(name) || name === 'search' || CART_EVENTS.includes(name);
  // GA4 is out of custom dimensions, so Light Finder puts the step in the event name
  // (lf_answer_room). PostHog has no such cap: one event, with the step as a property.
  const LIGHT_FINDER_STEP = /^lf_(step|answer|edit)_/;
  // posthog-js adds its campaign parameters to every event: utm_*, and ad click IDs that are
  // null unless the landing URL had them. $pageview and the session keep them for
  // attribution; on the forwarded GA4 events they only repeat it.
  const CAMPAIGN_KEY = /^(?:utm_[a-z]+|gad_source|mc_cid|gclid|gclsrc|dclid|gbraid|wbraid|fbclid|msclkid|twclid|li_fat_id|igshid|ttclid|rdt_cid|epik|qclid|sccid|oppref|bfcid|irclid|_kx)$/;
  const PERSONAL_KEY = /e-?mail|phone|address|first_?name|last_?name|full_?name/i;
  const EMAIL_VALUE = /[^\s@]+@[^\s@]+\.[^\s@]+/;
  const EMAIL_GLOBAL = /[^\s@&?=/]+(?:@|%40)[^\s@&?=/]+\.[a-z]{2,}/gi;
  const NO_REPLAY_PATH = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:account|checkouts?|orders)(?:\/|$)/i;
  const NO_AUTOCAPTURE_PATH = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:cart|account|checkouts?|orders)(?:\/|$)/i;
  const REPLAY_KEY = 'lamps:posthog-replay';
  const VISITOR_KEY = 'lamps:posthog-visitor';
  const OPTED_OUT_KEY = 'lamps:posthog-opted-out';
  // room-studio.js mints this id; the hash below is the one the View in Room Worker stores
  // as visitor_id and sends to PostHog as distinct_id.
  const ROOM_OWNER_KEY = 'lamps:room-owner:v1';
  // Linking creates a billed person profile, so it waits until the shopper opens View in
  // Room. The button impressions fire for nearly every PDP visitor, and room_share_* comes
  // from people looking at someone else's preview.
  const ROOM_STEP = /^room_(?!button_shown$|button_seen$|share_)/;
  const INTERNAL_KEY = 'lamps:posthog-internal';
  const RETURNING_KEY = 'lamps:posthog-returning';
  const KLAVIYO_STATE_KEY = 'klaviyo_profile_state';
  // Same ladder as AUDIENCES in helpers/shared/capture-widget.ts: first match wins.
  const AUDIENCES = ['trade', 'trade_prospect', 'repeat_buyer', 'subscriber', 'returning', 'new'];

  // Staff mark their own browsers with ?lamps_internal=1 (and clear it with =0) on any page,
  // before consent or PostHog has loaded.
  try {
    const internal = new URLSearchParams(location.search).get('lamps_internal');
    if (internal === '1') localStorage.setItem(INTERNAL_KEY, '1');
    if (internal === '0') localStorage.removeItem(INTERNAL_KEY);
  } catch { /* Optional storage. */ }

  // Analytics consent only, as Shopify's Customer Privacy API reports it. 'pending' until
  // the API has loaded, and while a required banner is still unanswered.
  function consentStatus() {
    const privacy = window.Shopify?.customerPrivacy;
    if (typeof privacy?.analyticsProcessingAllowed !== 'function') return 'pending';
    if (privacy.analyticsProcessingAllowed() !== false) return 'allowed';
    return privacy.shouldShowBanner?.() ? 'pending' : 'declined';
  }

  function whenConsentKnown(callback) {
    let done = false;
    let attempts = 0;
    const check = () => {
      if (done || consentStatus() === 'pending') return false;
      done = true;
      callback(consentStatus());
      return true;
    };
    if (check()) return;
    window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], check);
    document.addEventListener('visitorConsentCollected', check);
    (function poll() {
      if (check() || ++attempts >= 100) return;
      setTimeout(poll, 100);
    })();
  }

  // Nothing loads before the shopper does something, so PostHog stays off the page's
  // load and first-input path entirely.
  function afterFirstInteraction(callback) {
    const events = ['pointerdown', 'keydown', 'touchstart', 'scroll', 'wheel'];
    const start = () => {
      events.forEach((name) => window.removeEventListener(name, start, true));
      callback();
    };
    events.forEach((name) => window.addEventListener(name, start, { capture: true, passive: true }));
  }

  const idle = (callback) => (window.requestIdleCallback ? requestIdleCallback(callback, { timeout: 3000 }) : setTimeout(callback, 1));

  // One decision per tab session, so a recording doesn't stop halfway through a visit.
  function replaySampled() {
    if (!config.replay || NO_REPLAY_PATH.test(location.pathname)) return false;
    try {
      const saved = sessionStorage.getItem(REPLAY_KEY);
      if (saved === '1' || saved === '0') return saved === '1';
      const sampled = Math.random() < Number(config.replaySampleRate || 0);
      sessionStorage.setItem(REPLAY_KEY, sampled ? '1' : '0');
      return sampled;
    } catch {
      return false;
    }
  }

  // Decided once per tab session, before posthog-js writes its own storage, so the whole
  // first visit reads as new.
  function returningVisitor() {
    try {
      const saved = sessionStorage.getItem(RETURNING_KEY);
      if (saved === '1' || saved === '0') return saved === '1';
      const name = `ph_${config.token}_posthog`;
      const returning = !!localStorage.getItem(name) || document.cookie.split('; ').some((cookie) => cookie.startsWith(`${name}=`));
      sessionStorage.setItem(RETURNING_KEY, returning ? '1' : '0');
      return returning;
    } catch {
      return false;
    }
  }

  // klaviyo-tracking.js owns this; only whether a field is set is read, never its value.
  function klaviyoProfile() {
    try { return JSON.parse(localStorage.getItem(KLAVIYO_STATE_KEY)) || {}; } catch { return {}; }
  }

  function superProperties() {
    const profile = klaviyoProfile();
    const rungs = [
      config.audience,
      profile.tradePageViewedAt && 'trade_prospect',
      profile.identifiedEmail && 'subscriber',
      returningVisitor() ? 'returning' : 'new',
    ];
    let internal = false;
    try { internal = localStorage.getItem(INTERNAL_KEY) === '1'; } catch { /* Optional storage. */ }
    return {
      audience: AUDIENCES.find((audience) => rungs.includes(audience)),
      signed_in: config.signedIn === true,
      ...(config.customerType ? { customer_type: config.customerType } : {}),
      ...(internal ? { is_internal: true } : {}),
    };
  }

  // PostHog's snippet contract: window.posthog queues calls until array.js replaces it.
  function loadPostHog() {
    const stub = [];
    stub._i = [];
    stub.__SV = 1;
    ['capture', 'identify', 'alias', 'register', 'unregister', 'setPersonProperties', 'opt_in_capturing', 'opt_out_capturing', 'stopSessionRecording'].forEach((method) => {
      stub[method] = (...args) => stub.push([method, ...args]);
    });
    stub.init = (token, options, name) => stub._i.push([token, options, name]);
    window.posthog = stub;
    const script = document.createElement('script');
    script.async = true;
    script.crossOrigin = 'anonymous';
    // PostHog's own hosts serve array.js from a separate assets host. A proxy such as
    // https://vir.lamps.com/ph serves it under its own base.
    script.src = `${config.apiHost.replace(/\/$/, '').replace(/\.i\.posthog\.com$/, '-assets.i.posthog.com')}/static/array.js`;
    document.head.appendChild(script);
    return stub;
  }

  function clean(params) {
    const properties = {};
    Object.entries(params || {}).forEach(([key, value]) => {
      if (PERSONAL_KEY.test(key) || (typeof value === 'string' && EMAIL_VALUE.test(value))) return;
      properties[key] = value;
    });
    return properties;
  }

  // posthog-js adds its own properties ($current_url, $referrer, the page URL inside each web
  // vital, and as a key of $heatmap_data) after clean() has run, and a Klaviyo or search link
  // can carry an address in its query string.
  function scrub(object) {
    Object.keys(object).forEach((key) => {
      const value = object[key];
      const safeKey = key.replace(EMAIL_GLOBAL, '[redacted]');
      if (safeKey !== key) delete object[key];
      object[safeKey] = typeof value === 'string' ? value.replace(EMAIL_GLOBAL, '[redacted]') : value;
      if (value && typeof value === 'object') scrub(value);
    });
  }

  function redact(event) {
    if (!event?.properties) return event;
    if (forwarded(event.event)) {
      Object.keys(event.properties).filter((key) => CAMPAIGN_KEY.test(key)).forEach((key) => delete event.properties[key]);
    }
    scrub(event.properties);
    return event;
  }

  async function sha256(text) {
    const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    return Array.from(new Uint8Array(hash), (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  // Joins this browser's PostHog person to the View in Room Worker's server-side events and
  // D1 rows. Only reads the id, which room-studio.js mints.
  let linkedOwner = null;
  async function linkVisitor() {
    let owner;
    try { owner = localStorage.getItem(ROOM_OWNER_KEY); } catch { return; }
    if (!/^[a-f0-9]{32}$/.test(owner || '') || owner === linkedOwner || !crypto.subtle) return;
    linkedOwner = owner;
    const visitor = await sha256(`room-visitor:${owner}`);
    try { if (localStorage.getItem(VISITOR_KEY) === visitor) return; } catch { /* Optional storage. */ }
    window.posthog.alias(visitor);
    window.posthog.setPersonProperties({ room_visitor_id: visitor });
    try { localStorage.setItem(VISITOR_KEY, visitor); } catch { /* Optional storage. */ }
  }

  function forward(entry) {
    if (!entry || typeof entry !== 'object' || entry[0] !== 'event' || typeof entry[1] !== 'string') return;
    // An app addressing its own GA4 tag. Analyzify sends a second search this way on /search.
    if (entry[2]?.send_to) return;
    const properties = clean(entry[2]);
    const match = entry[1].match(LIGHT_FINDER_STEP);
    if (match) properties.step ||= entry[1].slice(match[0].length);
    const name = match ? `lf_${match[1]}` : entry[1];
    if (!forwarded(name)) return;
    if (ROOM_STEP.test(name)) linkVisitor();
    window.posthog.capture(name, properties);
  }

  function start() {
    const labels = superProperties();
    const posthog = loadPostHog();
    posthog.init(config.token, {
      api_host: config.apiHost,
      // Behind the proxy, the toolbar and its links still need PostHog's app.
      ui_host: 'https://us.posthog.com',
      person_profiles: 'identified_only',
      // Clicks, changes and submits on links, buttons and form fields only, with every
      // element's text and attributes masked. None at all where carts and orders are shown.
      autocapture: NO_AUTOCAPTURE_PATH.test(location.pathname) ? false : {
        dom_event_allowlist: ['click', 'change', 'submit'],
        element_allowlist: ['a', 'button', 'form', 'select', 'input'],
      },
      mask_all_text: true,
      mask_all_element_attributes: true,
      rageclick: false,
      capture_dead_clicks: false,
      capture_heatmaps: true,
      // LCP, INP, CLS and FCP per page. Network timing stays off.
      capture_performance: { web_vitals: true, network_timing: false },
      // Sentry owns errors.
      capture_exceptions: false,
      disable_surveys: true,
      enable_recording_console_log: false,
      disable_session_recording: !replaySampled(),
      // Templates that print a customer's name, email or address as text mark it data-ph-mask.
      session_recording: { maskAllInputs: true, maskTextSelector: '[contenteditable], [data-ph-mask]' },
      before_send: redact,
    });
    // A decline on an earlier page persists in PostHog's storage; reaching here means the
    // shopper has since allowed analytics.
    try {
      if (localStorage.getItem(OPTED_OUT_KEY)) {
        posthog.opt_in_capturing();
        localStorage.removeItem(OPTED_OUT_KEY);
      }
    } catch { /* Optional storage. */ }
    // Super properties persist in PostHog's storage, so a label that no longer applies (signed
    // out, staff flag cleared) has to be removed, not just left out.
    posthog.register(labels);
    ['customer_type', 'is_internal'].filter((key) => !(key in labels)).forEach((key) => posthog.unregister(key));
    if (config.customerId) posthog.identify(config.customerId);

    // Events sent before PostHog loaded are already in dataLayer; later ones pass through push.
    const dataLayer = (window.dataLayer = window.dataLayer || []);
    dataLayer.forEach(forward);
    const push = dataLayer.push;
    dataLayer.push = function (...entries) {
      const result = push.apply(this, entries);
      entries.forEach(forward);
      return result;
    };

    document.addEventListener('visitorConsentCollected', () => {
      if (consentStatus() !== 'declined') return;
      window.posthog.opt_out_capturing();
      window.posthog.stopSessionRecording();
      try { localStorage.setItem(OPTED_OUT_KEY, '1'); } catch { /* Optional storage. */ }
    });
  }

  afterFirstInteraction(() => whenConsentKnown((status) => {
    if (status === 'allowed') idle(start);
  }));
})();
